import { contentCellCount } from '@sharedlib/common/sheets/content-cells';

import { fill } from '@sharedlib/sheets-editor/sheets.strings';

/**
 * §11.5.2's action row: "`<n> sheets` | `<cell_count> cells` | `<k> tables` (the last part only
 * when k > 0)", with "1 sheet" when n = 1 (§11.5.1). The cells are §4.1's `cell_count`, by the
 * same rule the stored column uses.
 */
export interface WorkbookCounts {
  readonly sheets: number;
  readonly cells: number;
  readonly tables: number;
}

type Json = Record<string, unknown>;
const isObject = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);

export function workbookCounts(snapshot: unknown): WorkbookCounts | null {
  if (!isObject(snapshot) || !isObject(snapshot['sheets'])) return null;
  const order = Array.isArray(snapshot['sheetOrder']) ? snapshot['sheetOrder'] : Object.keys(snapshot['sheets']);
  const resources = Array.isArray(snapshot['resources']) ? snapshot['resources'] : [];
  const table = resources.find((r) => isObject(r) && r['name'] === 'SHEET_TABLE_PLUGIN') as Json | undefined;
  let tables = 0;
  try {
    const data = typeof table?.['data'] === 'string' ? (JSON.parse(table['data']) as unknown) : {};
    for (const sheet of Object.values(isObject(data) ? data : {})) if (isObject(sheet) && Array.isArray(sheet['tables'])) tables += sheet['tables'].length;
  } catch {
    // A table resource that isn't JSON counts none; ALG-VAL-DOC refuses such a workbook on save.
  }
  return { sheets: order.length, cells: contentCellCount(snapshot), tables };
}

/** The row's parts, in order; the tables only when there are some. */
export function workbookCountLabels(counts: WorkbookCounts): string[] {
  const n = (v: number) => v.toLocaleString('en-US');
  const parts = [
    counts.sheets === 1 ? fill('1 sheet') : fill('{n} sheets', { n: n(counts.sheets) }),
    counts.cells === 1 ? fill('1 cell') : fill('{n} cells', { n: n(counts.cells) }),
  ];
  if (counts.tables > 0) parts.push(counts.tables === 1 ? fill('1 table') : fill('{n} tables', { n: n(counts.tables) }));
  return parts;
}

/**
 * A template's row (Wook, 2026-10-06), as the Design Lab's detail view words it: "`<n> sheets` | Template", where a
 * workbook's names its cells and tables.
 */
export function templateCountLabels(counts: WorkbookCounts): string[] {
  const n = counts.sheets.toLocaleString('en-US');
  return [counts.sheets === 1 ? fill('1 sheet') : fill('{n} sheets', { n }), fill('Template')];
}
